import { Button, Form, Input, Select, Space, Switch } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect } from "react";
import SelectDropdownLgIcon from "../../../../../../assets/icons/SelectDropdownLgIcon";
import CustomModal from "../../../../../../components/ui/CustomModal";
import Loader from "../../../../../../components/ui/Loader";
import {
  useCreateNotificationTemplatesMutation,
  useGetNotificationTemplatesDetailsQuery,
  useUpdateNotificationTemplatesMutation,
} from "../../../../../../features/notification/notificationApi";
import useError from "../../../../../../hooks/useError";
import { toast } from "../../../../../../lib/toast";
import { ModalComponentProps } from "../../../../../../types";

export default function ManageMailTemplate({
  handleClose,
  type,
  data,
  ...rest
}: ModalComponentProps<number>) {
  const [form] = Form.useForm();
  const {
    isLoading: isDetailsLoading,
    isError: isDetailsDetailsError,
    error: detailsError,
    data: details,
  } = useGetNotificationTemplatesDetailsQuery(Number(data), {
    skip: !data && type !== "UPDATE",
  });
  const [
    createNotificationTemplates,
    { isLoading, isError, isSuccess, error },
  ] = useCreateNotificationTemplatesMutation();
  const [
    updateNotificationTemplates,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      isSuccess: isUpdateSuccess,
      error: updateError,
    },
  ] = useUpdateNotificationTemplatesMutation();
  // error toast message
  useError(isError, error, "Department added fail");
  useError(isUpdateError, updateError, "Department updated fail");
  useError(isDetailsDetailsError, detailsError, "Get department fail");

  const onFinish = (values: unknown) => {
    if (type === "UPDATE" && data) {
      return updateNotificationTemplates({ body: values, id: data });
    }

    return createNotificationTemplates(values);
  };

  useEffect(() => {
    if (Object.keys(details?.data || {}) && type === "UPDATE") {
      const detailsData = details?.data?.template;

      form.setFieldsValue({
        is_active: detailsData?.is_active,
        template_name: detailsData?.template_name,
        mail_subject: detailsData?.mail_subject,
        body: detailsData?.body,
      });
    }
  }, [form, details?.data, type]);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Mail template added successfully");
      return handleClose();
    }
    if (isUpdateSuccess) {
      toast("success", "Mail template updated successfully");
      return handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isUpdateSuccess]);

  return (
    <CustomModal
      title={`${type === "UPDATE" ? "Edit" : "Add"} Mail Template`}
      {...rest}
      handleClose={handleClose}
      width={660}
      footer={null}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        {isDetailsLoading ? (
          <Loader />
        ) : (
          <>
            <Form.Item
              name="is_active"
              className="sr-only"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="template_name"
              label="Template Name"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please enter name",
                },
              ]}
              initialValue={"attendance_summary"}
            >
              <Select
                options={templateList}
                suffixIcon={<SelectDropdownLgIcon />}
                placeholder="Select"
              />
            </Form.Item>
            <Form.Item
              name="mail_subject"
              label="Subject"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please enter subject",
                },
              ]}
            >
              <Input size="large" placeholder="Subject" />
            </Form.Item>
            <Form.Item
              name="body"
              label="Body"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please enter body",
                },
              ]}
            >
              <TextArea placeholder="Body text" rows={4} />
            </Form.Item>
          </>
        )}

        <Space className="w-full mt-2" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
            disabled={isLoading || isUpdateLoading}
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            loading={isLoading || isUpdateLoading}
            disabled={isDetailsLoading}
          >
            {type === "UPDATE" ? "Update" : "Save"}
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}

export const templateList = [
  {
    label: "Attendance Summary",
    value: "attendance_summary",
  },
];
